'use client';

import Link from 'next/link';
import { useState, useEffect, Suspense, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import brand from '@/config/brand';

// Get auth content from brand config with fallbacks
const authContent = brand.auth || {
	register: {
		title: 'Criar uma conta',
		description: 'Preencha os dados abaixo para se registrar',
		inviteDescription: 'Crie sua conta para aceitar o convite',
		subscriptionDescription: 'Crie sua conta para finalizar sua assinatura',
		inviteCodeLabel: 'Você está usando um código de convite',
		subscriptionLabel: 'Você selecionou um plano de assinatura',
	},
};

// Definir interface para erros de autenticação
interface AuthError {
	message?: string;
	code?: string;
	statusCode?: number;
}

// Componente que usa useSearchParams
function RegisterForm() {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [acceptTerms, setAcceptTerms] = useState(false);
	const [marketingConsent, setMarketingConsent] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [inviteCode, setInviteCode] = useState('');
	const [priceId, setPriceId] = useState('');
	const router = useRouter();
	const searchParams = useSearchParams();
	const { signUp } = useAuth();
	const { toast } = useToast();

	// Pegar o código de convite e priceId da URL, se existirem
	useEffect(() => {
		const code = searchParams.get('invite');
		const price = searchParams.get('priceId');

		if (code) {
			setInviteCode(code);
			// Mostrar feedback visual
			toast({
				title: 'Código de convite detectado',
				description: `Registre-se para aceitar o convite.`,
			});
		}

		if (!priceId && price) {
			setPriceId(price);
		}
	}, [priceId, searchParams, toast]);

	useEffect(() => {
		if (priceId) {
			toast({
				title: 'Plano selecionado',
				description: 'Você selecionou um plano de assinatura.',
			});
		}
	}, [priceId, toast]);

	const handleSignUp = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();

			if (!firstName || !lastName || !email || !password || !confirmPassword) {
				toast({
					title: 'Erro de validação',
					description: 'Por favor, preencha todos os campos.',
					variant: 'destructive',
				});
				return;
			}

			if (password !== confirmPassword) {
				toast({
					title: 'Erro de validação',
					description: 'As senhas não coincidem.',
					variant: 'destructive',
				});
				return;
			}

			if (!acceptTerms) {
				toast({
					title: 'Termos e Privacidade',
					description:
						'Você precisa de ler e aceitar os Termos de Serviço e a Política de Privacidade.',
					variant: 'destructive',
				});
				return;
			}

			try {
				setIsLoading(true);
                                const { error } = await signUp(email, password, {
                                        firstName,
                                        lastName,
                                        fullName: `${firstName} ${lastName}`,
                                        inviteCode: inviteCode || undefined,
                                        priceId: priceId || undefined,
                                        marketingConsent: marketingConsent,
                                });

                                if (error) {
                                        throw error;
                                }

                                // Disparar evento de registro concluído
                                try {
                                        await fetch('/api/complete-registration', { method: 'POST' })
                                } catch (err) {
                                        console.error('Falha ao enviar evento CompleteRegistration', err)
                                }

				toast({
					title: 'Conta criada com sucesso!',
					description: inviteCode
						? 'Verifique seu email para confirmar o cadastro e aceitar o convite.'
						: 'Verifique seu email para confirmar o cadastro.',
				});

				// Redirecionar para a página de verificação de email
				router.push('/auth/verify-email');
			} catch (error: unknown) {
				const authError = error as AuthError;

				let errorMessage =
					'Ocorreu um erro ao registrar sua conta. Tente novamente.';

				if (authError.message) {
					if (authError.message.includes('User already registered')) {
						errorMessage =
							'Email já registrado. Tente fazer login ou recuperar sua senha.';
					} else if (
						authError.message.includes('Password should be at least')
					) {
						errorMessage = 'A senha deve ter no mínimo 6 caracteres.';
					} else {
						errorMessage = authError.message;
					}
				}

				toast({
					title: 'Erro no registro',
					description: errorMessage,
					variant: 'destructive',
				});
			} finally {
				setIsLoading(false);
			}
		},
		[
			signUp,
			email,
			password,
			confirmPassword,
			acceptTerms,
			marketingConsent,
			inviteCode,
			priceId,
			router,
			toast,
		]
	);

	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12">
			<Card className="w-full max-w-md">
				<div className="absolute left-4 top-4">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => window.history.back()}
						aria-label="Voltar"
					>
						<ArrowLeft className="h-5 w-5" />
					</Button>
				</div>
				<CardHeader className="space-y-1 text-center">
					<CardTitle className="text-2xl font-bold">{authContent.register.title}</CardTitle>
					<CardDescription>
						{inviteCode
							? authContent.register.inviteDescription
							: priceId
								? authContent.register.subscriptionDescription
								: authContent.register.description}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<form onSubmit={handleSignUp} className="space-y-4">
						{inviteCode && (
							<div className="rounded-md bg-muted p-3">
								<p className="text-xs font-medium text-center">
									{authContent.register.inviteCodeLabel}
								</p>
							</div>
						)}

						{priceId && (
							<div className="rounded-md bg-muted p-3">
								<p className="text-xs font-medium text-center">
									{authContent.register.subscriptionLabel}
								</p>
							</div>
						)}

						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="firstName">Nome</Label>
								<Input
									id="firstName"
									placeholder="João"
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									required
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="lastName">Sobrenome</Label>
								<Input
									id="lastName"
									placeholder="Silva"
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									required
								/>
							</div>
						</div>
						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								type="email"
								placeholder="seu@email.com"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="password">Senha</Label>
							<Input
								id="password"
								type="password"
								placeholder="••••••••"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="confirmPassword">Confirmar Senha</Label>
							<Input
								id="confirmPassword"
								type="password"
								placeholder="••••••••"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								required
							/>
						</div>
						<div className="space-y-3">
							<div className="flex items-start space-x-2">
								<Checkbox
									id="terms"
									checked={acceptTerms}
									onCheckedChange={(checked) =>
										setAcceptTerms(checked as boolean)
									}
									aria-required="true"
								/>
								<Label htmlFor="terms" className="text-sm font-normal">
									Li e concordo com os{' '}
									<Link
										href="/termos-de-servico"
										className="text-primary hover:underline"
										target="_blank"
									>
										Termos de Serviço
									</Link>{' '}
									e a{' '}
									<Link
										href="/politica-de-privacidade"
										className="text-primary hover:underline"
										target="_blank"
									>
										Política de Privacidade
									</Link>
									.
								</Label>
							</div>

							<div className="flex items-center space-x-2">
								<Checkbox
									id="marketing"
									checked={marketingConsent}
									onCheckedChange={(checked) =>
										setMarketingConsent(checked as boolean)
									}
								/>
								<Label htmlFor="marketing" className="text-sm font-normal">
									Gostaria de receber emails com novidades e promoções.
								</Label>
							</div>
						</div>
						<Button
							type="submit"
							className="w-full"
							disabled={isLoading || !acceptTerms}
						>
							{isLoading ? 'Criando conta...' : 'Criar Conta'}
						</Button>
					</form>

					<div className="hidden relative my-4">
						<div className="absolute inset-0 flex items-center">
							<span className="w-full border-t"></span>
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-card px-2 text-muted-foreground">
								Ou continue com
							</span>
						</div>
					</div>

					<div className="hidden grid grid-cols-2 gap-4">
						<Button
							variant="outline"
							type="button"
							onClick={() =>
								toast({
									title: 'Recurso não disponível',
									description:
										'Registro com Google será implementado em breve.',
								})
							}
						>
							<svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
								<path
									d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
									fill="#4285F4"
								/>
								<path
									d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
									fill="#34A853"
								/>
								<path
									d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
									fill="#FBBC05"
								/>
								<path
									d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
									fill="#EA4335"
								/>
							</svg>
							Google
						</Button>
						<Button
							variant="outline"
							type="button"
							onClick={() =>
								toast({
									title: 'Recurso não disponível',
									description: 'Registro com Apple será implementado em breve.',
								})
							}
						>
							<svg
								className="mr-2 h-4 w-4"
								fill="currentColor"
								viewBox="0 0 24 24"
							>
								<path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z" />
							</svg>
							Apple
						</Button>
					</div>
				</CardContent>
				<CardFooter className="flex justify-center">
					<div className="text-sm text-muted-foreground">
						Já tem uma conta?{' '}
						<Link href="/auth/login" className="text-primary hover:underline">
							Faça login
						</Link>
					</div>
				</CardFooter>
			</Card>
		</div>
	);
}

// Componente de fallback enquanto carrega
function RegisterFallback() {
	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12">
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1 text-center">
					<CardTitle className="text-2xl font-bold">Criar uma conta</CardTitle>
					<CardDescription>Carregando...</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="h-20 flex items-center justify-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

// Componente principal que usa Suspense
export default function RegisterPage() {
	return (
		<Suspense fallback={<RegisterFallback />}>
			<RegisterForm />
		</Suspense>
	);
}
